import { Component, ElementRef, forwardRef, HostListener, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ResourceClientService } from 'src/app/core/services/resource-client.service';
import { ResponseHandlerService } from 'src/app/services/response.handlers.service';

export const DEFAULT_PROFILE_IMAGE = 'assets/img/default-profile.jpg';

/**
 * Circular profile picture field backed by the resource controller.
 *
 * Uploads the selected file, then reports the stored file URL back to the parent
 * form, so it can be bound with formControlName like any other input:
 *
 *   <app-profile-picture formControlName="ProfilePicture"></app-profile-picture>
 */
@Component({
  selector: 'app-profile-picture',
  templateUrl: './profile-picture.component.html',
  styleUrls: ['./profile-picture.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProfilePictureComponent),
      multi: true
    }
  ]
})
export class ProfilePictureComponent implements ControlValueAccessor {

  @Input() label = 'Profile Picture';
  @Input() maxSizeInMb = 5;

  /** Renders a small avatar with overlay action badges, sized to sit inside a table row. */
  @Input() compact = false;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  readonly defaultImage = DEFAULT_PROFILE_IMAGE;

  imagePreview: string = DEFAULT_PROFILE_IMAGE;
  isLoading = false;
  isDisabled = false;
  isPreviewOpen = false;

  /** View and remove are only offered once there is something other than the placeholder. */
  get hasImage(): boolean {
    return this.imagePreview !== this.defaultImage;
  }

  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  constructor(
    private resourceClient: ResourceClientService,
    private responseHandler: ResponseHandlerService
  ) { }

  // ================= ControlValueAccessor =================

  writeValue(value: string): void {
    this.resetPreview();

    if (value) {
      this.loadStoredImage(value);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  // ================= Actions =================

  openFilePicker(): void {
    if (this.isDisabled) {
      return;
    }
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    this.onTouched();

    if (!file) {
      return;
    }

    if (!this.isValidImage(file)) {
      this.clearFileInput();
      return;
    }

    this.showLocalPreview(file);
    this.uploadImage(file);
  }

  deleteImage(): void {
    if (this.isDisabled) {
      return;
    }

    this.resetPreview();
    this.clearFileInput();
    this.onChange('');
    this.onTouched();
  }

  /** Opens the uploaded image full size, since the avatar is too small to check it by eye. */
  openPreview(): void {
    if (!this.hasImage) {
      return;
    }
    this.isPreviewOpen = true;
  }

  closePreview(): void {
    this.isPreviewOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closePreview();
  }

  /** A stored URL that no longer resolves should not leave a broken image in the form. */
  onImageError(): void {
    this.imagePreview = this.defaultImage;
    this.isLoading = false;
  }

  // ================= Helpers =================

  private isValidImage(file: File): boolean {
    if (!file.type.startsWith('image/')) {
      this.responseHandler.showValidation('Please select a valid image file.');
      return false;
    }

    const maxSizeInBytes = this.maxSizeInMb * 1024 * 1024;

    if (file.size > maxSizeInBytes) {
      this.responseHandler.showValidation(
        `Image size must be ${this.maxSizeInMb} MB or less.`
      );
      return false;
    }

    return true;
  }

  /** Shows the picked file immediately, while the upload is still in flight. */
  private showLocalPreview(file: File): void {
    this.isLoading = true;

    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreview = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  private uploadImage(file: File): void {
    const formData = new FormData();
    formData.append('ImageFile', file, file.name);

    this.resourceClient.upload(formData).subscribe({
      next: (response: any) => {
        this.isLoading = false;

        const uploadedUrl = this.extractFileUrl(response);

        if (!uploadedUrl) {
          this.responseHandler.showError('Image upload failed. Please try again.');
          this.deleteImage();
          return;
        }

        this.onChange(uploadedUrl);
      },
      error: (err: any) => {
        this.isLoading = false;
        this.responseHandler.handleHttpError(err);
        this.deleteImage();
      }
    });
  }

  /**
   * The upload response carries the stored URL as fileUrls, which may be an array
   * of URLs or a single string. Only one file is ever uploaded here, so take the first.
   */
  private extractFileUrl(response: any): string {
    const fileUrls = response?.fileUrls ?? response?.FileUrls ?? '';
    const fileUrl = Array.isArray(fileUrls) ? fileUrls[0] : fileUrls;

    if (typeof fileUrl !== 'string') {
      return '';
    }

    return fileUrl.replace(/^Image URL:\s*/, '').trim();
  }

  /** Stored values may be a directly bindable URL, or a file name that has to be fetched. */
  private loadStoredImage(value: string): void {
    if (/^(https?:\/\/|data:|assets\/)/i.test(value)) {
      this.imagePreview = value;
      return;
    }

    this.isLoading = true;

    this.resourceClient.fetchProfilePicture(value).subscribe({
      next: (dataUrl: string) => {
        this.imagePreview = dataUrl;
        this.isLoading = false;
      },
      error: () => {
        this.imagePreview = this.defaultImage;
        this.isLoading = false;
      }
    });
  }

  private resetPreview(): void {
    this.imagePreview = this.defaultImage;
    this.isLoading = false;
    this.isPreviewOpen = false;
  }

  private clearFileInput(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }
}
